import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useLLMTask } from "../hooks/useLLMTask";
import {
  Brain,
  Loader2,
  Zap,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Target,
  Shield,
  Sparkles,
  ChevronRight,
  RefreshCw,
  Play,
  Clock,
  CheckCircle,
  FileText,
  BarChart3,
  Lightbulb,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Calendar,
  BookOpen
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom Gauge Component for Scores
const ScoreGauge = ({ score, label, type, darkMode }) => {
  const isOpportunity = type === "opportunity";
  const color = isOpportunity 
    ? score > 70 ? '#10b981' : score > 40 ? '#f59e0b' : '#ef4444'
    : score > 70 ? '#ef4444' : score > 40 ? '#f59e0b' : '#10b981';
  
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <svg width="120" height="120" viewBox="0 0 120 120">
          <circle
            cx="60"
            cy="60"
            r="45"
            fill="none"
            stroke={darkMode ? '#21262d' : '#e5e7eb'}
            strokeWidth="10"
          />
          <circle
            cx="60"
            cy="60"
            r="45"
            fill="none"
            stroke={color}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            transform="rotate(-90 60 60)"
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
          <text
            x="60"
            y="55"
            textAnchor="middle"
            fill={darkMode ? 'white' : '#111827'}
            fontSize="24"
            fontWeight="bold"
          >
            {score || 0}
          </text>
          <text
            x="60"
            y="72"
            textAnchor="middle"
            fill={darkMode ? '#8b949e' : '#6b7280'}
            fontSize="10"
          >
            /100
          </text>
        </svg>
      </div>
      <p className={`text-sm font-medium mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        {label}
      </p>
    </div>
  );
};

export default function PredictiveAI() {
  const { darkMode, region, brand } = useOutletContext() || { darkMode: true, region: 'UAE', brand: 'blackswan' };
  const [predictions, setPredictions] = useState(null);
  const [playbooks, setPlaybooks] = useState([]);
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState("predictions");
  const [simulateDialogOpen, setSimulateDialogOpen] = useState(false);
  const [scenarioInput, setScenarioInput] = useState({
    type: "competitive_move",
    description: ""
  });

  // Async task hooks for heavy PREMIUM-tier generations that exceed the 55s
  // inline ingress window. Each kicks off a background task and polls.
  const playbookTask = useLLMTask();
  const simulationTask = useLLMTask();
  const generatingPlaybook = playbookTask.isRunning;
  const simulatingScenario = simulationTask.isRunning;

  const fetchData = useCallback(async () => {
    try {
      const [predictionsRes, playbooksRes, simulationsRes] = await Promise.all([
        axios.get(`${API}/automation/predictions/${brand}/${region}`),
        axios.get(`${API}/automation/playbooks/${brand}/${region}`),
        axios.get(`${API}/automation/simulations/${brand}/${region}`)
      ]);
      setPredictions(predictionsRes.data.latest || null);
      setPlaybooks(playbooksRes.data.playbooks || []);
      setSimulations(simulationsRes.data.simulations || []);
    } catch (error) {
      console.error("Error fetching predictive data:", error);
    } finally {
      setLoading(false);
    }
  }, [brand, region]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const generatePredictions = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/automation/predictions/generate/${brand}/${region}`);
      setPredictions(res.data.prediction);
      toast.success("Predictions generated!");
    } catch (error) {
      toast.error("Failed to generate predictions");
    } finally {
      setGenerating(false);
    }
  };

  const generatePlaybook = async () => {
    await playbookTask.run({
      endpoint: `/automation/playbooks/generate-async/${brand}/${region}?playbook_type=comprehensive`,
      onDone: () => fetchData(),
    });
  };

  const runSimulation = async () => {
    if (!scenarioInput.description) {
      toast.error("Please describe the scenario");
      return;
    }
    await simulationTask.run({
      endpoint: `/automation/simulate-async/${brand}/${region}`,
      body: scenarioInput,
      onDone: () => {
        setSimulateDialogOpen(false);
        setScenarioInput({ type: "competitive_move", description: "" });
        fetchData();
      },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="predictive-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#DC143C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="predictive-ai-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Predictive AI & Strategy
          </h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            AI-powered predictions, strategic playbooks, and scenario simulations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={simulateDialogOpen} onOpenChange={setSimulateDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className={darkMode ? 'border-[#30363d]' : ''}>
                <Play className="w-4 h-4 mr-2" />
                Simulate Scenario
              </Button>
            </DialogTrigger>
            <DialogContent className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
              <DialogHeader>
                <DialogTitle className={darkMode ? 'text-white' : ''}>What-If Scenario Simulation</DialogTitle>
                <DialogDescription>
                  Describe a scenario and let AI predict the outcomes
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className={darkMode ? 'text-gray-300' : ''}>Scenario Type</Label>
                  <Select 
                    value={scenarioInput.type}
                    onValueChange={(v) => setScenarioInput({...scenarioInput, type: v})}
                  >
                    <SelectTrigger className={darkMode ? 'bg-[#21262d] border-[#30363d] text-white' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={darkMode ? 'bg-[#21262d] border-[#30363d]' : ''}>
                      <SelectItem value="competitive_move" className={darkMode ? 'text-white' : ''}>Competitive Move</SelectItem>
                      <SelectItem value="market_change" className={darkMode ? 'text-white' : ''}>Market Change</SelectItem>
                      <SelectItem value="internal_action" className={darkMode ? 'text-white' : ''}>Internal Action</SelectItem>
                      <SelectItem value="external_event" className={darkMode ? 'text-white' : ''}>External Event</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className={darkMode ? 'text-gray-300' : ''}>Describe the Scenario</Label>
                  <Textarea 
                    value={scenarioInput.description}
                    onChange={(e) => setScenarioInput({...scenarioInput, description: e.target.value})}
                    placeholder="e.g., What if our main competitor launches a 30% price cut?"
                    className={`min-h-[100px] ${darkMode ? 'bg-[#21262d] border-[#30363d] text-white' : ''}`}
                  />
                </div>
                <Button 
                  onClick={runSimulation}
                  disabled={simulatingScenario || !scenarioInput.description}
                  data-testid="run-simulation-btn"
                  className="w-full bg-[#DC143C] hover:bg-[#b91031]"
                >
                  {simulatingScenario ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Brain className="w-4 h-4 mr-2" />
                  )}
                  {simulatingScenario
                    ? `Simulating in background… (${simulationTask.elapsedSec}s)`
                    : "Run Simulation"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          <Button
            onClick={generatePredictions}
            disabled={generating}
            className="bg-[#DC143C] hover:bg-[#b91031]"
            data-testid="generate-predictions-btn"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Generate Predictions
          </Button>
        </div>
      </div>

      {/* Score Cards */}
      {predictions && (
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-8">
            <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
              <CardContent className="p-6">
                <div className="flex items-center justify-around">
                  <ScoreGauge 
                    score={predictions.opportunity_score} 
                    label="Opportunity Score" 
                    type="opportunity"
                    darkMode={darkMode}
                  />
                  <div className={`h-24 w-px ${darkMode ? 'bg-[#30363d]' : 'bg-gray-200'}`} />
                  <ScoreGauge 
                    score={predictions.threat_score} 
                    label="Threat Score" 
                    type="threat"
                    darkMode={darkMode}
                  />
                  <div className={`h-24 w-px ${darkMode ? 'bg-[#30363d]' : 'bg-gray-200'}`} />
                  <div className="text-center">
                    <div className={`text-3xl font-bold ${
                      predictions.market_outlook === 'bullish' ? 'text-emerald-500' :
                      predictions.market_outlook === 'bearish' ? 'text-red-500' :
                      'text-amber-500'
                    }`}>
                      {predictions.market_outlook === 'bullish' ? (
                        <TrendingUp className="w-12 h-12 mx-auto" />
                      ) : predictions.market_outlook === 'bearish' ? (
                        <TrendingDown className="w-12 h-12 mx-auto" />
                      ) : (
                        <Minus className="w-12 h-12 mx-auto" />
                      )}
                    </div>
                    <p className={`text-sm font-medium mt-2 capitalize ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {predictions.market_outlook || 'Neutral'} Outlook
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="col-span-4">
            <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-lg ${darkMode ? 'text-white' : ''}`}>
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Forecast
                </CardTitle>
              </CardHeader>
              <CardContent>
                {predictions.forecast && (
                  <div className="space-y-3">
                    <div>
                      <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Next 30 Days</p>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {predictions.forecast.next_30_days}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Next 90 Days</p>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {predictions.forecast.next_90_days}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={darkMode ? 'bg-[#161b22]' : ''}>
          <TabsTrigger value="predictions">Opportunities & Threats</TabsTrigger>
          <TabsTrigger value="playbooks">Strategic Playbooks</TabsTrigger>
          <TabsTrigger value="simulations">Scenario Simulations</TabsTrigger>
        </TabsList>

        {/* Predictions Tab */}
        <TabsContent value="predictions" className="mt-4">
          {predictions ? (
            <div className="grid grid-cols-2 gap-6">
              {/* Opportunities */}
              <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
                <CardHeader>
                  <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
                    <ArrowUpRight className="w-5 h-5 text-emerald-500" />
                    Opportunities
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {predictions.opportunities?.map((opp, idx) => (
                    <div 
                      key={idx}
                      className={`p-4 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}
                    >
                      <div className="flex items-start justify-between">
                        <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {opp.title}
                        </h4>
                        <Badge className={`border-0 ${
                          opp.potential_impact === 'high' ? 'bg-emerald-500/10 text-emerald-500' :
                          opp.potential_impact === 'medium' ? 'bg-amber-500/10 text-amber-500' :
                          'bg-blue-500/10 text-blue-500'
                        }`}>
                          {opp.potential_impact}
                        </Badge>
                      </div>
                      <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {opp.description}
                      </p>
                      <div className="flex items-center gap-2 mt-3">
                        <Badge variant="outline" className="text-xs">
                          <Clock className="w-3 h-3 mr-1" />
                          {opp.time_horizon}
                        </Badge>
                        <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {Math.round((opp.probability || 0) * 100)}% probability
                        </span>
                      </div>
                      {opp.recommended_actions && (
                        <div className="mt-3 pt-3 border-t border-dashed" style={{ borderColor: darkMode ? '#30363d' : '#e5e7eb' }}>
                          <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Recommended Actions:
                          </p>
                          <ul className={`text-xs mt-1 space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {opp.recommended_actions.slice(0, 2).map((action, i) => (
                              <li key={`opp-${opp.id || opp.opportunity || i}-action-${i}`} className="flex items-start gap-1">
                                <ChevronRight className="w-3 h-3 mt-0.5 text-emerald-500" />
                                {action}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Threats */}
              <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
                <CardHeader>
                  <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
                    <ArrowDownRight className="w-5 h-5 text-red-500" />
                    Threats
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {predictions.threats?.map((threat, idx) => (
                    <div 
                      key={idx}
                      className={`p-4 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}
                    >
                      <div className="flex items-start justify-between">
                        <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {threat.title}
                        </h4>
                        <Badge className={`border-0 ${
                          threat.severity === 'critical' ? 'bg-red-500/10 text-red-500' :
                          threat.severity === 'high' ? 'bg-orange-500/10 text-orange-500' :
                          threat.severity === 'medium' ? 'bg-amber-500/10 text-amber-500' :
                          'bg-blue-500/10 text-blue-500'
                        }`}>
                          {threat.severity}
                        </Badge>
                      </div>
                      <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {threat.description}
                      </p>
                      <div className="flex items-center gap-2 mt-3">
                        <Badge variant="outline" className="text-xs">
                          <Clock className="w-3 h-3 mr-1" />
                          {threat.time_horizon}
                        </Badge>
                        <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {Math.round((threat.probability || 0) * 100)}% probability
                        </span>
                      </div>
                      {threat.mitigation_strategies && (
                        <div className="mt-3 pt-3 border-t border-dashed" style={{ borderColor: darkMode ? '#30363d' : '#e5e7eb' }}>
                          <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Mitigation Strategies:
                          </p>
                          <ul className={`text-xs mt-1 space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {threat.mitigation_strategies.slice(0, 2).map((strategy, i) => (
                              <li key={`threat-${threat.id || threat.threat || i}-strat-${i}`} className="flex items-start gap-1">
                                <Shield className="w-3 h-3 mt-0.5 text-amber-500" />
                                {strategy}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
              <CardContent className="p-12 text-center">
                <Brain className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  No predictions generated yet
                </h3>
                <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Click "Generate Predictions" to get AI-powered opportunity and threat analysis
                </p>
                <Button 
                  className="mt-6 bg-[#DC143C] hover:bg-[#b91031]"
                  onClick={generatePredictions}
                  disabled={generating}
                >
                  {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Generate Predictions
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Playbooks Tab */}
        <TabsContent value="playbooks" className="mt-4">
          <div className="flex justify-end items-center gap-3 mb-4">
            {generatingPlaybook && (
              <span
                data-testid="playbook-elapsed"
                className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
              >
                Generating in background… {playbookTask.elapsedSec}s elapsed
              </span>
            )}
            <Button
              onClick={generatePlaybook}
              disabled={generatingPlaybook}
              data-testid="generate-playbook-btn"
              className="bg-[#DC143C] hover:bg-[#b91031]"
            >
              {generatingPlaybook ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <BookOpen className="w-4 h-4 mr-2" />
              )}
              {generatingPlaybook ? `Working… (${playbookTask.elapsedSec}s)` : "Generate Playbook"}
            </Button>
          </div>

          {playbooks.length > 0 ? (
            <div className="space-y-4">
              {playbooks.map((playbook) => (
                <Card key={playbook.id} className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <CardTitle className={darkMode ? 'text-white' : ''}>
                          {playbook.title}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {playbook.executive_summary}
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge className="bg-[#DC143C]/10 text-[#DC143C] border-0">
                          {playbook.playbook_type}
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`${API}/export/playbook/${playbook.id}/pdf`, '_blank')}
                          className={`gap-1 ${darkMode ? 'border-[#30363d] text-gray-300 hover:bg-[#21262d]' : ''}`}
                          data-testid={`export-playbook-pdf-${playbook.id}`}
                        >
                          <FileText className="w-3 h-3" /> PDF
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`${API}/export/playbook/${playbook.id}/csv`, '_blank')}
                          className={`gap-1 ${darkMode ? 'border-[#30363d] text-gray-300 hover:bg-[#21262d]' : ''}`}
                          data-testid={`export-playbook-csv-${playbook.id}`}
                        >
                          <FileText className="w-3 h-3" /> CSV
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Strategic Pillars */}
                    {playbook.strategic_pillars && (
                      <div className="mb-6">
                        <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          Strategic Pillars
                        </h4>
                        <div className="grid grid-cols-3 gap-3">
                          {playbook.strategic_pillars.slice(0, 3).map((pillar, idx) => (
                            <div 
                              key={`${playbook.id || 'pb'}-pillar-${pillar.pillar || pillar.name || idx}`}
                              className={`p-3 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}
                            >
                              <p className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {pillar.pillar}
                              </p>
                              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {pillar.objective}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* KPIs */}
                    {playbook.kpis && (
                      <div className="mb-6">
                        <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          Key Performance Indicators
                        </h4>
                        <div className="grid grid-cols-4 gap-3">
                          {playbook.kpis.slice(0, 4).map((kpi, idx) => (
                            <div 
                              key={`${playbook.id || 'pb'}-kpi-${kpi.metric || kpi.name || idx}`}
                              className={`p-3 rounded-lg text-center ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}
                            >
                              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {kpi.metric}
                              </p>
                              <p className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {kpi.target}
                              </p>
                              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {kpi.timeline}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Generated {(() => {
                        const ts = playbook.created_at || playbook.generated_at;
                        if (!ts) return 'recently';
                        const d = new Date(ts);
                        return isNaN(d.getTime()) ? 'recently' : d.toLocaleDateString();
                      })()}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
              <CardContent className="p-12 text-center">
                <BookOpen className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  No playbooks generated yet
                </h3>
                <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Generate an AI-powered strategic playbook with actionable recommendations
                </p>
                <Button 
                  className="mt-6 bg-[#DC143C] hover:bg-[#b91031]"
                  onClick={generatePlaybook}
                  disabled={generatingPlaybook}
                >
                  {generatingPlaybook ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BookOpen className="w-4 h-4 mr-2" />}
                  Generate Playbook
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Simulations Tab */}
        <TabsContent value="simulations" className="mt-4">
          {simulations.length > 0 ? (
            <div className="space-y-4">
              {simulations.map((sim) => (
                <Card key={sim.id} className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <Badge className="mb-2 bg-purple-500/10 text-purple-500 border-0">
                          {sim.scenario_type}
                        </Badge>
                        <CardTitle className={darkMode ? 'text-white' : ''}>
                          {sim.scenario_title}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {sim.input_scenario?.description}
                        </CardDescription>
                      </div>
                      <div className="text-right">
                        <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          Confidence
                        </p>
                        <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {Math.round((sim.confidence_level || 0) * 100)}%
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Impact Analysis */}
                    {sim.impact_analysis && (
                      <div className="grid grid-cols-4 gap-4 mb-4">
                        <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Market Share</p>
                          <p className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {sim.impact_analysis.market_share_change}
                          </p>
                        </div>
                        <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Position</p>
                          <p className={`font-bold capitalize ${
                            sim.impact_analysis.competitive_position_change === 'improved' ? 'text-emerald-500' :
                            sim.impact_analysis.competitive_position_change === 'weakened' ? 'text-red-500' :
                            darkMode ? 'text-white' : 'text-gray-900'
                          }`}>
                            {sim.impact_analysis.competitive_position_change}
                          </p>
                        </div>
                        <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>New Opp Score</p>
                          <p className="font-bold text-emerald-500">
                            {sim.impact_analysis.new_opportunity_score}
                          </p>
                        </div>
                        <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>New Threat Score</p>
                          <p className="font-bold text-red-500">
                            {sim.impact_analysis.new_threat_score}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Recommended Responses */}
                    {sim.recommended_responses && (
                      <div>
                        <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          Recommended Responses
                        </h4>
                        <div className="space-y-2">
                          {sim.recommended_responses.slice(0, 3).map((resp, idx) => (
                            <div 
                              key={`${sim.id || 'sim'}-resp-${resp.action || resp.name || idx}`}
                              className={`p-3 rounded-lg flex items-center justify-between ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}
                            >
                              <div className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-emerald-500" />
                                <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {resp.response}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  {resp.timing}
                                </Badge>
                                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {Math.round((resp.expected_effectiveness || 0) * 100)}% effective
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className={`text-xs mt-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Simulated {new Date(sim.simulated_at).toLocaleString()}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
              <CardContent className="p-12 text-center">
                <Play className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  No simulations run yet
                </h3>
                <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Run what-if scenario simulations to predict outcomes of different strategies
                </p>
                <Button 
                  className="mt-6 bg-[#DC143C] hover:bg-[#b91031]"
                  onClick={() => setSimulateDialogOpen(true)}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Run Your First Simulation
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
